import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
  Input, Button, Badge,
} from '@werkstatt/ui';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/ui/form';
import { Building2, Info } from 'lucide-react';

const schema = z.object({
  name: z.string().min(1, 'Pflichtfeld'),
  email: z.string().email('Ungültige E-Mail').optional().or(z.literal('')),
  phone: z.string().optional(),
  street: z.string().optional(),
  postal_code: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface TenantSettings {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  street?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  subscription_plan: string;
  subscription_status: string;
}

export default function Settings() {
  const queryClient = useQueryClient();

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await api.get<TenantSettings>('/settings/');
      return res.data;
    },
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', phone: '', street: '', postal_code: '', city: '', country: 'DE' },
  });

  useEffect(() => {
    if (settings) {
      form.reset({
        name: settings.name,
        email: settings.email || '',
        phone: settings.phone || '',
        street: settings.street || '',
        postal_code: settings.postal_code || '',
        city: settings.city || '',
        country: settings.country || 'DE',
      });
    }
  }, [settings, form]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => api.put('/settings/', values),
    onSuccess: () => {
      toast.success('Einstellungen gespeichert');
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: () => toast.error('Fehler beim Speichern'),
  });

  return (
    <MainLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-3xl font-bold">Einstellungen</h1>
          <p className="text-muted-foreground mt-1">Unternehmens- und Systemeinstellungen</p>
        </div>

        {settings && (
          <Card variant="glass">
            <CardHeader>
              <CardTitle className="font-display text-sm font-medium">Abonnement</CardTitle>
            </CardHeader>
            <CardContent className="flex gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Plan</p>
                <Badge>{settings.subscription_plan}</Badge>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                <Badge variant={settings.subscription_status === 'active' ? 'default' : 'destructive'}>
                  {settings.subscription_status}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        <Card variant="glass">
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Unternehmensdaten
            </CardTitle>
            <CardDescription>Grundlegende Informationen Ihres Unternehmens</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
                <FormField control={form.control} name="name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Firmenname *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="email" render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-Mail</FormLabel>
                      <FormControl><Input type="email" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="phone" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefon</FormLabel>
                      <FormControl><Input {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <FormField control={form.control} name="street" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Straße & Hausnummer</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <div className="grid grid-cols-3 gap-4">
                  <FormField control={form.control} name="postal_code" render={({ field }) => (
                    <FormItem>
                      <FormLabel>PLZ</FormLabel>
                      <FormControl><Input {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="city" render={({ field }) => (
                    <FormItem className="col-span-2">
                      <FormLabel>Ort</FormLabel>
                      <FormControl><Input {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <Button type="submit" disabled={mutation.isPending}>
                  {mutation.isPending ? 'Speichern...' : 'Einstellungen speichern'}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        <Card variant="glass">
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2">
              <Info className="h-5 w-5" />
              Arbeitszeitregeln
            </CardTitle>
            <CardDescription>Informationen zu den Zeiterfassungsregeln</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>Die Soll-Stunden werden pro Mitarbeiter individuell festgelegt (Mitarbeiterverwaltung → Soll-Stunden/Woche).</p>
            <p>Überstunden werden automatisch berechnet: Ist-Zeit minus Soll-Zeit für den jeweiligen Zeitraum.</p>
            <p>Wochenstunden werden auf Arbeitstage (Mo–Fr) aufgeteilt.</p>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
